import { paths } from '@common/types';
import { ColumnSelectionFilter } from './ColumnSelectionFilter';
import {
  useDeletePatientTablePreferencesMutation,
  usePatientTablePreferencesQuery,
  useUpdatePatientTablePreferencesMutation,
} from '@common/cache';
import { Button } from '@mui/material';

type PatientTableColumns =
  paths['/preferences/patient-table']['get']['responses']['200']['content']['application/json']['columns'];

type PatientTableColumnKey = keyof PatientTableColumns;

const DefaultHiddenColumn: PatientTableColumnKey = 'practice_name';

const PATIENT_TABLE_COLUMN_LABELS: Record<PatientTableColumnKey, string> = {
  id: 'ID',
  name: 'Name',
  birthdate: 'Date of birth',
  minutes_this_month: 'CM Minutes',
  mbi: 'Primary insurance',
  account_status: 'Account status',
  guide_status: 'GUIDE status',
  programs: 'Programs',
  provider_name: 'PCP',
  practice_name: 'Practice',
  care_navigator_name: 'Care navigator',
  building_name: 'Location',
  open_task_count: 'Open tasks',
};

export const PatientTableColumnSelectionFilter = ({
  hideText,
}: {
  hideText?: boolean;
}) => {
  const { data: preferences, isPending: isFetchPending } =
    usePatientTablePreferencesQuery();
  const updatePreferencesMutation = useUpdatePatientTablePreferencesMutation();
  const deletePreferencesMutation = useDeletePatientTablePreferencesMutation();

  const handleSelectionChange = async (selection: PatientTableColumns) => {
    updatePreferencesMutation.mutateAsync({ columns: selection });
  };

  const handleReset = async () => {
    if (!preferences?.columns) return;

    const resetColumns = Object.fromEntries(
      Object.entries(preferences.columns).map(([key, config]) => [
        key,
        {
          ...config,
          column_display: key === DefaultHiddenColumn ? 'hidden' : 'display',
        },
      ]),
    ) as PatientTableColumns;

    await updatePreferencesMutation.mutateAsync({ columns: resetColumns });
  };

  return (
    preferences?.columns && (
      <ColumnSelectionFilter
        name='patient_table_columns'
        label='Columns'
        hideText={hideText}
        columns={preferences?.columns}
        columnLabels={PATIENT_TABLE_COLUMN_LABELS}
        onChange={handleSelectionChange}
        footerAction={
          <Button color='primary' onClick={handleReset}>
            RESET
          </Button>
        }
        loading={
          updatePreferencesMutation.isPending ||
          deletePreferencesMutation.isPending ||
          isFetchPending
        }
      />
    )
  );
};
